import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useEvents } from '../../contexts/EventContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import { formatDate, formatTime, getSpotsRemaining, isEventStillVisible } from '../../utils/helpers';
import { getCategoryLabel, getCategoryColor, RegistrationAttendee } from '../../types';
import QRCodeDisplay from '../../components/QRCodeDisplay';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { events, getUserRegistrations } = useEvents();
  const { user } = useAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState<{
    registrationId: string;
    eventTitle: string;
    attendees?: RegistrationAttendee[];
  } | null>(null);

  // Get user's registered events
  const userRegistrations = user ? getUserRegistrations(user.id) : [];
  const confirmedRegistrations = userRegistrations.filter((r) => r.status === 'confirmed');
  const registeredEventIds = confirmedRegistrations.map((r) => r.eventId);

  // Create a map from eventId to registration for quick lookup
  const registrationByEventId = confirmedRegistrations.reduce((acc, reg) => {
    acc[reg.eventId] = reg;
    return acc;
  }, {} as Record<string, typeof confirmedRegistrations[0]>);

  const registeredEvents = events
    .filter((e) => registeredEventIds.includes(e.id) && isEventStillVisible(e.date, e.endTime))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Get upcoming events (excluding already registered ones)
  const upcomingEvents = events
    .filter((e) => e.isActive && isEventStillVisible(e.date, e.endTime) && !registeredEventIds.includes(e.id))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const handleViewAllEvents = () => {
    navigate(ROUTES.EVENTS);
  };

  const handleViewEvent = (eventId: string, fromMyRegistrations = false) => {
    navigate(`/events/${eventId}`, fromMyRegistrations ? { state: { from: 'my-registrations' } } : undefined);
  };

  const scrollToSection = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const sectionWidth = container.offsetWidth;
      container.scrollTo({
        left: sectionWidth * index,
        behavior: 'smooth',
      });
      setActiveSection(index);
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const sectionWidth = container.offsetWidth;
      const newSection = Math.round(container.scrollLeft / sectionWidth);
      if (newSection !== activeSection) {
        setActiveSection(newSection);
      }
    }
  };

  // Handle QR code click
  const handleQrClick = (
    e: React.MouseEvent,
    registrationId: string,
    eventTitle: string,
    attendees?: RegistrationAttendee[]
  ) => {
    e.stopPropagation();
    setQrData({ registrationId, eventTitle, attendees });
    setQrDialogOpen(true);
  };

  // Event card component
  const EventCard = ({ event, isRegistered = false, registration }: { event: typeof events[0]; isRegistered?: boolean; registration?: typeof confirmedRegistrations[0] }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      }}
      onClick={() => handleViewEvent(event.id, isRegistered)}
    >
      <Box
        sx={{
          height: 8,
          backgroundColor: getCategoryColor(event.category),
        }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Chip
            label={getCategoryLabel(event.category)}
            size="small"
            sx={{
              backgroundColor: getCategoryColor(event.category),
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
          {isRegistered && registration?.checkedIn && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 14, color: 'white !important' }} />}
              label="Checked In"
              size="small"
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontSize: '0.7rem',
              }}
            />
          )}
          {isRegistered && !registration?.checkedIn && (
            <Chip
              icon={<BookmarkIcon sx={{ fontSize: 14, color: 'white !important' }} />}
              label="Registered"
              size="small"
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
          {event.title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {formatDate(event.date)} at {formatTime(event.startTime)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }} noWrap>
            {event.location}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        {!isRegistered && (
          <Typography
            variant="caption"
            color={
              getSpotsRemaining(event.capacity, event.currentAttendees) > 0
                ? 'success.main'
                : 'error.main'
            }
          >
            {getSpotsRemaining(event.capacity, event.currentAttendees)} spots remaining
          </Typography>
        )}
        {isRegistered && (
          <Typography variant="caption" color="primary">
            View details →
          </Typography>
        )}
        {isRegistered && registration && (
          <IconButton
            size="small"
            onClick={(e) => handleQrClick(e, registration.id, event.title, registration.attendees)}
            sx={{ color: '#00b7ed' }}
            title="Show QR Codes"
          >
            <QrCodeIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Box>
      {/* Welcome Section */}
      <Box
        sx={{
          py: 3,
          px: 2,
          mb: 3,
          backgroundColor: 'rgba(0, 183, 237, 0.05)',
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 1,
            color: '#333',
          }}
        >
          Welcome back, {user?.firstName}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse events and manage your registrations
        </Typography>
      </Box>

      {/* Section Tabs */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        <Chip
          icon={<BookmarkIcon sx={{ color: activeSection === 0 ? 'white !important' : '#4caf50 !important' }} />}
          label="My Events"
          onClick={() => scrollToSection(0)}
          sx={{
            backgroundColor: activeSection === 0 ? '#4caf50' : 'transparent',
            color: activeSection === 0 ? 'white' : '#4caf50',
            border: '1px solid #4caf50',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: activeSection === 0 ? '#43a047' : 'rgba(76, 175, 80, 0.1)',
            },
          }}
        />
        <Chip
          icon={<EventIcon sx={{ color: activeSection === 1 ? 'white !important' : '#00b7ed !important' }} />}
          label="Upcoming Events"
          onClick={() => scrollToSection(1)}
          sx={{
            backgroundColor: activeSection === 1 ? '#00b7ed' : 'transparent',
            color: activeSection === 1 ? 'white' : '#00b7ed',
            border: '1px solid #00b7ed',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: activeSection === 1 ? '#0095c4' : 'rgba(0, 183, 237, 0.1)',
            },
          }}
        />
      </Box>

      {/* Swipeable Sections Container */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* Section 1: My Events */}
        <Box
          sx={{
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            flexShrink: 0,
            scrollSnapAlign: 'start',
            boxSizing: 'border-box',
            overflow: 'hidden',
            pr: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              mb: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BookmarkIcon sx={{ color: '#4caf50' }} />
              <Typography variant="h6" fontWeight={600}>
                My Events
              </Typography>
              <Chip
                label={registeredEvents.length}
                size="small"
                sx={{ backgroundColor: '#4caf50', color: 'white', height: 24, minWidth: 24 }}
              />
            </Box>
            <IconButton
              onClick={() => scrollToSection(1)}
              sx={{ color: '#00b7ed', display: { xs: 'none', sm: 'flex' } }}
              title="View Upcoming Events"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>

          {registeredEvents.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
              }}
            >
              <BookmarkIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                You haven't registered for any upcoming events yet.
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => scrollToSection(1)}
                sx={{ mt: 2 }}
              >
                Browse Upcoming Events
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {registeredEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <EventCard event={event} isRegistered registration={registrationByEventId[event.id]} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Section 2: Upcoming Events */}
        <Box
          sx={{
            width: '100%',
            minWidth: '100%',
            maxWidth: '100%',
            flexShrink: 0,
            scrollSnapAlign: 'start',
            boxSizing: 'border-box',
            overflow: 'hidden',
            pl: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              mb: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => scrollToSection(0)}
                sx={{ color: '#4caf50', display: { xs: 'none', sm: 'flex' } }}
                title="View My Events"
              >
                <ArrowBackIcon />
              </IconButton>
              <EventIcon sx={{ color: '#00b7ed' }} />
              <Typography variant="h6" fontWeight={600}>
                Upcoming Events
              </Typography>
              <Chip
                label={upcomingEvents.length}
                size="small"
                sx={{ backgroundColor: '#00b7ed', color: 'white', height: 24, minWidth: 24 }}
              />
            </Box>
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewAllEvents}
              size="small"
            >
              View All
            </Button>
          </Box>

          {upcomingEvents.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
              }}
            >
              <EventIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No upcoming events at the moment.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back soon for new events!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {upcomingEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <EventCard event={event} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* Swipe indicator dots */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: 3,
        }}
      >
        <Box
          onClick={() => scrollToSection(0)}
          sx={{
            width: activeSection === 0 ? 24 : 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: activeSection === 0 ? '#4caf50' : '#ccc',
            cursor: 'pointer',
            transition: 'all 0.3s',
          }}
        />
        <Box
          onClick={() => scrollToSection(1)}
          sx={{
            width: activeSection === 1 ? 24 : 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: activeSection === 1 ? '#00b7ed' : '#ccc',
            cursor: 'pointer',
            transition: 'all 0.3s',
          }}
        />
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, textAlign: 'center' }}
      >
        Swipe or use tabs to switch sections
      </Typography>

      {/* QR Code Dialog */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Event Check-in QR Code
          <IconButton onClick={() => setQrDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {qrData && (
            <QRCodeDisplay
              registrationId={qrData.registrationId}
              attendees={qrData.attendees}
              eventTitle={qrData.eventTitle}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HomePage;
